/**
 * What a field user's Today/Schedule/History card does with the `sourceRoute`
 * the API hands it.
 *
 * `unified-field.service.ts` builds these paths against its own naming, not the
 * router's: `/loads/:id`, `/rentals/deliveries/:id`, `/rentals/pickups/:id`,
 * `/rentals/inspections/:id`, `/forms/pre-job-meeting/:id`. None of the five is
 * a registered route. The `/my-work` tree ends in a catch-all `<Route path="*">`,
 * so tapping a card did not 404 — it bounced the user back to Today with no
 * explanation, which reads as a dead card rather than an unbuilt screen.
 *
 * Translating here rather than in `TaskCard` keeps one copy for the three tabs
 * that render task cards, and keeps the untranslatable cases in the same place
 * as the translatable ones.
 */

/**
 * The router path for an API `sourceRoute`, or `null` when no screen serves it.
 *
 * A `null` is a real answer and must not be navigated to — the caller shows the
 * card as unopenable instead of sending the user to the catch-all.
 *
 * An ALLOWLIST, and the two shapes with no screen are covered by falling off
 * the end of it rather than by an entry naming them. A denylist naming
 * `/forms/pre-job-meeting/:id` and `/rentals/inspections/:id` was written first
 * and deleted: every one of its entries already reached the same `null` through
 * the fallback, so removing the whole block changed no result and broke no
 * test. It read as a guard while being decoration. The two shapes are real
 * though, and worth naming here, because the reason each is unserved is not
 * obvious from the path:
 *
 *   /forms/pre-job-meeting/:id  — the API links a SUBMISSION by id.
 *                                 PreJobMeetingForm mints a fresh submission id
 *                                 on mount and reads no route param; there is
 *                                 no submission viewer to open.
 *   /rentals/inspections/:id    — /field/rentals/inspections is a list, with no
 *                                 detail route behind it.
 *
 * Adding either one means adding a screen AND a branch below, not an entry in a
 * list somewhere.
 */
export function resolveTaskRoute(sourceRoute: string | undefined | null): string | null {
  const raw = (sourceRoute ?? '').trim();
  if (!raw) return null;

  // `/loads/:id` → the driver load screen. `/driver/load/:loadId`, singular, is
  // the one built on the accept/depart/arrive/deliver mutation hooks; the
  // plural `/driver/loads/:loadId` is the older screen beside it.
  const load = /^\/loads\/([^/]+)$/.exec(raw);
  if (load) return `/driver/load/${load[1]}`;

  const delivery = /^\/rentals\/deliveries\/([^/]+)$/.exec(raw);
  if (delivery) return `/field/rentals/delivery/${delivery[1]}`;

  const pickup = /^\/rentals\/pickups\/([^/]+)$/.exec(raw);
  if (pickup) return `/field/rentals/return/${pickup[1]}`;

  return null;
}
